import {
  NewButton,
  NewButtonSize,
  NewButtonVariant,
  SlidingPanel,
} from "@visiblethread/react-components";
import EmptyState from "components/EmptyState";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import { USAGE_STATS_ACTIVITY } from "constants/routes";
import { modalTypes } from "constants/types";
import ActivityFeedRow from "layout/activityLog/ActivityFeedRow";
import { ENTITY_TYPE_SUBTITLE } from "layout/activityLog/constants";
import { buildItemActivityRoute } from "layout/activityLog/helpers";
import { History } from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchEntityActivity,
  selectEntityFeed,
} from "slices/activityLog";
import { showModal } from "slices/app/actions/modals";
import { selectHasUnsavedChanges } from "slices/app/selectors";

import styles from "./EntityActivityPanel.module.scss";

const EntityActivityPanel = ({ entityType, entityId, ownerType, ownerId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const feed = useSelector((state) =>
    selectEntityFeed(state, entityType, entityId)
  );
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);

  const navigateOrPromptUnsaved = (to) => {
    if (hasUnsavedChanges) {
      dispatch(showModal(modalTypes.HAS_UNSAVED_CHANGES, { to }));
    } else {
      navigate(to);
    }
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(
        fetchEntityActivity({
          entityType,
          entityId,
          ownerType,
          ownerId,
          offset: 0,
        })
      );
    }
  }, [dispatch, isOpen, entityType, entityId, ownerType, ownerId]);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const itemActivityRoute = buildItemActivityRoute(ownerType, ownerId);
  const subtitle =
    `Recent activity ${ENTITY_TYPE_SUBTITLE[entityType] ?? ""}`.trim();

  return (
    <>
      <NewButton
        onClick={handleOpen}
        variant={NewButtonVariant.l2}
        size={NewButtonSize.md}
        leftIcon={<History size={16} />}
        data-testid="entity-activity-panel-trigger"
      />
      {isOpen && (
        <SlidingPanel>
          <SlidingPanel.Header onHide={handleClose}>
            <Flex
              direction={DIRECTION_CLASS.column}
              alignItems={ALIGN_CLASS["flex-start"]}
              gap={GAP_CLASS.GAP_100}
            >
              <span className={styles.title}>Activity</span>
              <span className={styles.entityActivityPanel__subtitle}>
                {subtitle}
              </span>
            </Flex>
          </SlidingPanel.Header>
          <SlidingPanel.Body>
            <div
              className={styles.entityActivityPanel__body}
              data-testid="entity-activity-panel"
            >
              {feed.rows.length === 0 && !feed.isLoading ? (
                <EmptyState
                  icon={
                    <History size={24} color="var(--token-surface-gray-600)" />
                  }
                  title="No Activity Yet"
                  description="No activity has been recorded for this report yet."
                />
              ) : (
                <ul
                  className={styles.entityActivityPanel__list}
                  data-testid="entity-activity-list"
                >
                  {feed.rows.map((row) => (
                    <ActivityFeedRow key={row.id} row={row} />
                  ))}
                </ul>
              )}
            </div>
          </SlidingPanel.Body>
          <SlidingPanel.Footer>
            <Flex
              justifyContent="flex-end"
              alignItems={ALIGN_CLASS.stretch}
              gap={GAP_CLASS.GAP_200}
              className={styles.entityActivityPanel__footerNav}
            >
              <NewButton
                variant={NewButtonVariant.l2}
                onClick={() => navigateOrPromptUnsaved(USAGE_STATS_ACTIVITY)}
              >
                View Workspace Activity
              </NewButton>
              <NewButton
                variant={NewButtonVariant.l2}
                onClick={() =>
                  itemActivityRoute && navigateOrPromptUnsaved(itemActivityRoute)
                }
                disabled={!itemActivityRoute}
              >
                View Item Activity
              </NewButton>
            </Flex>
          </SlidingPanel.Footer>
        </SlidingPanel>
      )}
    </>
  );
};

EntityActivityPanel.propTypes = {
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  ownerType: PropTypes.string.isRequired,
  ownerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default EntityActivityPanel;
